import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaDna, FaVial, FaChartBar } from "react-icons/fa";

import MainTemplate from "../../../components/MainTemplate/MainTemplate";
import CardBackground from "../../../components/MainTemplate/CardBackground";
import HLATypingMatchResultComponent from "./HLATypingMatchResultComponent";
import SingleAntigenBeadTest from "./SingleAntigenBeadTest";
import CREGAnalysisComponent from "./CREGAnalysisComponent";

const DeepAnalyze3WayComponent = () => {
  let navigate = useNavigate();
  const { state } = useLocation();
  const cycle = state;

  const [activeTab, setActiveTab] = useState(0);
  const [expandedSections, setExpandedSections] = useState({});

  if (!cycle) {
    return (
      <MainTemplate>
        <CardBackground>
          <div className="p-8 text-center">
            <h2 className="text-2xl font-bold text-red-600">No cycle data available</h2>
            <p className="mt-2 text-gray-600">
              Please navigate from a 3-Way Match result.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 mt-4 text-white bg-gray-500 rounded hover:bg-gray-600"
            >
              Go Back
            </button>
          </div>
        </CardBackground>
      </MainTemplate>
    );
  }

  const pairs = [
    {
      label: "Pair 1: R1 ← D2",
      recipient: cycle.pair1?.recipient,
      donor: cycle.pair2?.donor,
      id: "pair1",
    },
    {
      label: "Pair 2: R2 ← D3",
      recipient: cycle.pair2?.recipient,
      donor: cycle.pair3?.donor,
      id: "pair2",
    },
    {
      label: "Pair 3: R3 ← D1",
      recipient: cycle.pair3?.recipient,
      donor: cycle.pair1?.donor,
      id: "pair3",
    },
  ];

  const currentPair = pairs[activeTab];

  const getTabStyles = (index) => {
    const colors = [
      { active: "bg-blue-500 text-white", inactive: "bg-gray-200 text-gray-700 hover:bg-gray-300", border: "border-blue-500", bg: "bg-blue-50" },
      { active: "bg-green-500 text-white", inactive: "bg-gray-200 text-gray-700 hover:bg-gray-300", border: "border-green-500", bg: "bg-green-50" },
      { active: "bg-orange-500 text-white", inactive: "bg-gray-200 text-gray-700 hover:bg-gray-300", border: "border-orange-500", bg: "bg-orange-50" },
    ];
    return colors[index] || colors[0];
  };

  const styles = getTabStyles(activeTab);

  const getFullName = (person) => {
    if (!person) return "N/A";
    if (person.name) return person.name;
    return `${person.member_title || ""} ${
      person.donor_name || person.recipient_name || ""
    } ${person.last_name || ""}`.trim() || "N/A";
  };

  const getBloodGroup = (person) =>
    person?.blood_group?.label || person?.bloodgroup || "N/A";

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <MainTemplate>
      <CardBackground>
        <div className="w-full">
          {/* Header */}
          <div className="p-4 mb-6 bg-gray-100 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-800">
                3-Way Deep Analyze
              </h1>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center px-4 py-2 text-white transition duration-300 bg-gray-500 rounded-md hover:bg-gray-600"
              >
                <FaArrowLeft className="mr-2" />
                Back
              </button>
            </div>
          </div>

          {/* Cycle overview */}
          <div className="grid grid-cols-1 gap-3 mb-6 md:grid-cols-3">
            {pairs.map((p, idx) => (
              <div
                key={p.id}
                className={`p-3 rounded-lg shadow-sm border ${
                  idx === activeTab
                    ? "border-purple-400 bg-purple-50"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <p className="text-xs font-semibold text-gray-500">{p.label}</p>
                <p className="text-sm mt-1">
                  <span className="font-medium">R:</span> {getFullName(p.recipient)} ({getBloodGroup(p.recipient)})
                </p>
                <p className="text-sm">
                  <span className="font-medium">D:</span> {getFullName(p.donor)} ({getBloodGroup(p.donor)})
                </p>
              </div>
            ))}
          </div>

          {/* Tab buttons */}
          <div className="flex mb-4 space-x-2">
            {pairs.map((p, idx) => {
              const s = getTabStyles(idx);
              return (
                <button
                  key={p.id}
                  onClick={() => setActiveTab(idx)}
                  className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition duration-300 ${
                    idx === activeTab ? s.active : s.inactive
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Active tab content */}
          {currentPair && (
            <div
              className={`p-6 rounded-b-lg border-t-4 ${styles.border} ${styles.bg}`}
            >
              <div className="space-y-6">
                {/* SAB Test */}
                <Section
                  title="Single Antigen Bead Test"
                  icon={<FaVial className="text-xl text-green-500" />}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  sectionId="sab"
                >
                  <SingleAntigenBeadTest
                    recipient={currentPair.recipient?._id}
                    donor={currentPair.donor?._id}
                    recipientName={getFullName(currentPair.recipient)}
                    donorName={getFullName(currentPair.donor)}
                  />
                </Section>

                {/* HLA Match */}
                <Section
                  title="HLA Match Details"
                  icon={<FaDna className="text-xl text-blue-500" />}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  sectionId="hla"
                >
                  <HLATypingMatchResultComponent
                    recipient={currentPair.recipient?._id}
                    donor={currentPair.donor?._id}
                    recipientName={getFullName(currentPair.recipient)}
                    donorName={getFullName(currentPair.donor)}
                  />
                </Section>

                {/* CREG Analysis */}
                <Section
                  title="CREG Analysis"
                  icon={<FaChartBar className="text-xl text-purple-500" />}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  sectionId="creg"
                >
                  <CREGAnalysisComponent
                    recipient={currentPair.recipient?._id}
                    donor={currentPair.donor?._id}
                    recipientName={getFullName(currentPair.recipient)}
                    donorName={getFullName(currentPair.donor)}
                  />
                </Section>
              </div>
            </div>
          )}
        </div>
      </CardBackground>
    </MainTemplate>
  );
};

const Section = ({
  title,
  icon,
  children,
  expandedSections,
  toggleSection,
  sectionId,
}) => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    <button
      className="flex items-center justify-between w-full p-4 bg-gray-50 hover:bg-gray-100 focus:outline-none"
      onClick={() => toggleSection(sectionId)}
    >
      <div className="flex items-center">
        {icon}
        <h3 className="ml-2 text-lg font-semibold text-gray-800">{title}</h3>
      </div>
      <span className="text-xl">
        {expandedSections[sectionId] ? "−" : "+"}
      </span>
    </button>
    {expandedSections[sectionId] && <div className="p-4">{children}</div>}
  </div>
);

export default DeepAnalyze3WayComponent;
